import { Grid } from '@material-ui/core'
import { Form, FormSpy } from 'react-final-form'
import useTranslate from '../../../../../../i18n/useTranslate'
import useSynthesis from './useSynthesis'
import MyAccordion from '../../../../../../control/accordion/accordion'
import Autocomplete from '../../../../../../control/autocomplete/autocomplete'
import MyTextfield from '../../../../../../control/textfield/textfield'
import PageAction from '../../pageAction'
import Dialog from '../../../../../../control/dialog/dialog'
import DisplayUpdater from '../../../../shareUI/displayUpdater/displayUpdater'
import ModeAccordion from '../../../../shareUI/modeAccordion/ModeAccordion'
import { useEffect, useRef } from 'react'

type SynthesisViewType = ReturnType<typeof useSynthesis>
const SynthesisView = (props: SynthesisViewType) => {
    const {
        isViewMode,
        configDialog,
        consideration,
        initialValues,
        isShowUpdateDate,
        useAccordionMode,
        formRef,
        updateAfterSubmitted,
        onRefDialog,
        onSubmitForm,
        onDirtyChange,
        onClickBackToSearch,
        onAutoSave,
    } = props
    const translate = useTranslate()

    const renderComment = (isDisabled: boolean) => (
        <Grid container spacing={2}>
            <Grid item xs={12} md={12} lg={12} xl={12}>
                <Autocomplete
                    name="approveStatus"
                    materialInputProps={{
                        label: translate.get('LABEL.SELECT_ETHIC_REVIEW_OUTCOME'),
                        placeholder: translate.get('LABEL.PLEASE_SELECT'),
                        disabled: isDisabled,
                    }}
                    options={consideration}
                    required
                />
            </Grid>
            <Grid item xs={12} md={12} lg={12} xl={12}>
                <MyTextfield
                    name={`synthesis`}
                    materialInputProps={{
                        disabled: isDisabled,
                        label: translate.get('LABEL.SUGGESTION_NOTICE'),
                        placeholder: translate.get('LABEL.REMARK'),
                        size: 'small',
                        rows: 5,
                        multiline: true,
                    }}
                />
            </Grid>
        </Grid>
    )

    // ---------- autosave ----------
    // เดิมมี 2 ตัวทำงานพร้อมกัน
    //   1) setInterval ทุก 5 นาที ซึ่งไม่ได้เช็ค lastAutoSaveTime เลย
    //   2) debounce 1 นาที ตอนฟอร์มเปลี่ยน
    // อาจจะยิงซ้อนกันได้ และไม่มีอะไรกันไม่ให้ยิงรอบใหม่ทั้งที่รอบก่อนยังไม่เสร็จ
    // รวมให้เหลือทางเข้าเดียวคือ runAutoSave
    const autoSaveTimer = useRef<any>(null)
    const lastAutoSaveTime = useRef<number>(0)
    const mountTime = useRef(Date.now())
    const isAutoSavingRef = useRef(false)

    const runAutoSave = async (values: any) => {
        if (!values) return
        // กันยิงซ้อนตอนรอบก่อนหน้ายังไม่เสร็จ
        if (isAutoSavingRef.current) return

        const now = Date.now()
        // ไม่ autosave ใน 1 นาทีแรกหลังเปิดหน้า
        if (now - mountTime.current < 60 * 1000) return
        // เว้นระยะอย่างน้อย 1 นาทีจากรอบที่แล้ว
        if (now - lastAutoSaveTime.current < 60 * 1000) return

        isAutoSavingRef.current = true
        lastAutoSaveTime.current = now
        try {
            await onAutoSave(values)
        } finally {
            isAutoSavingRef.current = false
        }
    }

    useEffect(() => {
        mountTime.current = Date.now()
        lastAutoSaveTime.current = 0

        return () => {
            if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleAutoSave = (values: any) => {
        if (!values) return

        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current)
        }

        autoSaveTimer.current = setTimeout(() => {
            const latestValues = formRef.current?.getState?.()?.values ?? values
            runAutoSave(latestValues)
        }, 60 * 1000)
    }


    return (
        <Form onSubmit={onSubmitForm} initialValues={{ ...initialValues }} keepDirtyOnReinitialize={true}>
            {(props) => {
                const { form, dirty } = props
                formRef.current = form
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12} lg={12}>
                            {useAccordionMode ? (
                                <ModeAccordion
                                    title={translate.get('LABEL.ETHIC_REVIEW_OUTCOME')}
                                    renderEditComponent={() => renderComment(false)}
                                    renderViewComponent={() => renderComment(true)}
                                    isViewMode={true}
                                    fieldNames={['approveStatus', 'synthesis']}
                                    onConfirm={() => updateAfterSubmitted(['approveStatus', 'synthesis'])}
                                />
                            ) : (
                                <MyAccordion title={translate.get('LABEL.ETHIC_REVIEW_OUTCOME')}>
                                    {renderComment(isViewMode)}
                                </MyAccordion>
                            )}
                            {isShowUpdateDate && <DisplayUpdater />}
                            {!isViewMode && (
                                <Grid item xs={12} md={12} lg={12} xl={12}>
                                    <PageAction
                                        onClickSaveDraft={() => {
                                            props.form.change('submitType', 'draft')
                                            props.handleSubmit()
                                        }}
                                        onClickSave={() => {
                                            props.form.change('submitType', 'save')
                                            props.handleSubmit()
                                        }}
                                        onClickClose={() => {
                                            onClickBackToSearch(dirty)
                                        }}
                                    />
                                </Grid>
                            )}
                            <FormSpy subscription={{ dirty: true, values: true }} onChange={(state) => {
                                                                                        onDirtyChange(state)
                                                                                        handleAutoSave(state.values)
                                                                                    }} />
                            <Dialog onRef={onRefDialog} {...configDialog} />
                        </Grid>
                    </Grid>
                )
            }}
        </Form>
    )
}

export type SynthesisType = {
    onSaveComplete?: (resStatus: number, isDraft: boolean) => void
    projectData: any
}
const Synthesis = (props: SynthesisType) => {
    const synthesis = useSynthesis(props)
    return <SynthesisView {...synthesis} />
}
export default Synthesis
